/**
 * The lookup that can make what it cannot find.
 *
 * You type into the field itself; the menu under it lists what matched, each
 * row with a 26px lead, a name and a second line of facts that tell two
 * similar answers apart ("Keystone Builders · Harrisburg · 3 properties" is
 * not "Keys & Sons Paving · York"). The last row is always the way out:
 *
 *     ＋ Create "Keys" as a new company
 *
 * That row is the reason this component exists. Every form in this product
 * asks you to name something that lives in another table — a category, a
 * vendor, a company, a property, an equipment type — and the old answer was a
 * dropdown that either had what you wanted or sent you to a different screen
 * to add it, losing everything you had typed. Creating it here creates it for
 * real, selects it, and leaves the form exactly as it was.
 *
 * Drawn from `24-record-flyouts/html/flyout-field-kit.html` (the Company
 * field) and `flyout-new-material.html` (the Category field): menu radius 12,
 * 6px vertical padding, rows inset 6px with radius 8, the first match on
 * `accent-soft`, and the create row divided off by a `line-faint` rule.
 */
import {
  Fragment, useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode,
} from 'react'
import { createPortal } from 'react-dom'
import { cn } from '../../lib/cn.js'
import { placeFloating } from '../../lib/place-floating.js'
import { controlClass } from './field.js'
import { IconPlus, IconSearch } from '../../frame/icons.js'

export interface ComboItem {
  id: string
  /** The bold line. */
  name: string
  /** The muted line under it — what tells two of these apart. */
  sub?: string
  /**
   * The 26px lead: an avatar, a `KindTile`, a glyph — the building in
   * `flyout-field-kit.html`'s Company menu.
   *
   * Left out, the row has no lead box at all rather than an empty grey
   * square: a placeholder where a thing's face should be reads as a thing
   * that failed to load.
   */
  lead?: ReactNode
  /**
   * A heading this row sits under — "Recent", "All properties".
   *
   * Drawn once, above the first row that carries it, in the section-label
   * shape (§3.2: 10.5/600, uppercase, faint). It exists for one case: a
   * lookup that has not been typed into yet shows what this person last
   * opened *and* the top of the list underneath, and without a rule between
   * them the two read as one badly-sorted list.
   *
   * Left off every row — which is what a typed search does — nothing is
   * drawn at all.
   */
  group?: string
  /**
   * Named facts the picker needs *back* rather than printed on the row — the
   * new-job form reads `meta.accountName` off the property it just picked to
   * fill in "Customer" underneath it.
   *
   * It is not drawn. A row that needs to *say* something says it in `sub`.
   */
  meta?: Record<string, string>
}

/**
 * What a search can answer with, when a bare list is not enough.
 *
 * A lookup over four hundred properties shows twenty-five of them, and
 * twenty-five rows with nothing said about the other three hundred and
 * seventy-five is a list that looks complete and is not — you scroll to the
 * bottom, do not find it, and conclude it is not on file. `note` is the line
 * that says otherwise: "25 of 412 — keep typing to narrow it".
 */
export interface ComboResult {
  items: ComboItem[]
  /** A muted line under the rows. Nothing is drawn when it is absent. */
  note?: string
}

export interface ComboInputProps {
  /** What is chosen now, drawn in the field when it is not being typed in. */
  value: ComboItem | null
  onPick: (item: ComboItem) => void
  /**
   * What matches. Called as the person types, debounced by the caller if it
   * hits the network; an empty query should return the first page rather
   * than nothing, so opening the field shows something.
   */
  search: (query: string) =>
    Promise<ComboItem[] | ComboResult> | ComboItem[] | ComboResult
  /**
   * Make one from what was typed, and select it.
   *
   * Left out, the field is a search and nothing more. Given, the menu's last
   * row offers it — and the noun in that row is `thing`.
   */
  onCreate?: (name: string) => Promise<ComboItem | null>
  /** "company", "category", "property" — the word in the create row. */
  thing: string
  label: string
  placeholder?: string
  id?: string
  describedBy?: string
  invalid?: boolean
  disabled?: boolean
  /** Said in the menu when nothing matched and nothing can be created. */
  empty?: string
  /**
   * The create row opens something — a form in its own sheet — rather than
   * making the record from the typed name alone.
   *
   * It changes one thing: the menu closes as the row is pressed, instead of
   * sitting there saying "Creating…" behind whatever opened. Inline creates
   * (a company, a category) keep the busy row, because they finish in a
   * moment and the row is where the answer appears.
   */
  createOpens?: boolean
  /** `touch`: the 44px phone field beside the other 44px fields (`new-request.md` Board 14). */
  size?: 'default' | 'touch'
}

const ROW_HEIGHT = 42

/**
 * The tallest a lookup menu is drawn, however much room there is.
 *
 * Twenty-five rows at 42px is a thousand pixels, and a dropdown that takes
 * the whole window has stopped being a dropdown. Ten rows is enough to see
 * that there is a list and enough to scan it; the rest scrolls, and the
 * footer says how many there are.
 */
const MENU_MAX = 10 * ROW_HEIGHT

/**
 * How tall the menu would be if nothing were in its way.
 *
 * Not `panel.scrollHeight`, which is what this used to ask, and which is a
 * trap here: the rows live in an inner scroller, so the panel's own
 * scrollHeight is whatever the panel has *already been capped to*. Feed that
 * back into the placement and the cap ratchets — the menu opens with one row
 * in it, is floored to 180, and then every later measurement reads back 180
 * and stays there however many rows arrive. That is exactly what the Property
 * lookup did: four rows visible out of twenty-five, with 600px of empty
 * screen above it.
 *
 * The content's height is the scroller's `scrollHeight` (unclamped, because
 * an `overflow-y-auto` box reports all of it) plus the chrome around it —
 * the padding and the create row — which is the difference between the two
 * clientHeights.
 */
function naturalHeight(panel: HTMLElement | null, scroller: HTMLElement | null): number {
  if (!panel || !scroller) return 0
  return scroller.scrollHeight + (panel.clientHeight - scroller.clientHeight)
}

export function ComboInput({
  value, onPick, search, onCreate, thing, label, placeholder = 'Search…',
  id, describedBy, invalid, disabled, empty, createOpens, size,
}: ComboInputProps) {
  const fallbackId = useId()
  const inputId = id ?? fallbackId
  const listId = `${inputId}-list`

  const [query, setQuery] = useState('')
  const [typing, setTyping] = useState(false)
  const [items, setItems] = useState<ComboItem[]>([])
  const [note, setNote] = useState<string | null>(null)
  const [active, setActive] = useState(0)
  const [busy, setBusy] = useState(false)
  const [open, setOpen] = useState(false)

  const field = useRef<HTMLSpanElement>(null)
  const typedInto = useRef<HTMLInputElement>(null)
  const panel = useRef<HTMLDivElement>(null)
  const scroller = useRef<HTMLDivElement>(null)
  const [box, setBox] = useState<
    { left: number; top: number; width: number; maxHeight: number } | null>(null)

  /*
   * The menu is portalled and positioned from the field's rectangle rather
   * than drawn inside it. A form lives in a `Sheet` whose body scrolls, and an
   * absolutely-placed menu inside a scrolling box is a menu with its bottom
   * half clipped off — the same reason `MenuSelect` does this.
   *
   * `placeFloating` is what keeps it on the window. This used to be
   * `top: rect.bottom + 4` and nothing else, so a lookup two thirds of the way
   * down a 620 flyout — which is where the Property and Primary contact
   * lookups both sit — opened straight off the bottom of the screen.
   *
   * It re-places whenever the rows change, because the menu's height is what
   * decides which way it goes: one row fits under anything, eight do not.
   */

  useEffect(() => {
    if (!open) return
    const outside = (event: MouseEvent) => {
      const target = event.target as Node
      if (field.current?.contains(target) || panel.current?.contains(target)) return
      close()
    }
    document.addEventListener('mousedown', outside)
    return () => document.removeEventListener('mousedown', outside)
  })

  /* What matched, re-asked whenever the query changes while the menu is up. */
  useEffect(() => {
    if (!open) return
    let live = true
    void Promise.resolve(search(query)).then((found) => {
      if (!live) return
      const result = Array.isArray(found) ? { items: found } : found
      setItems(result.items)
      setNote(result.note ?? null)
      setActive(0)
    }).catch(() => { if (live) { setItems([]); setNote(null) } })
    return () => { live = false }
  }, [open, query, search])

  const trimmed = query.trim()
  /*
   * The create row is always the last row, typed into or not.
   *
   * It used to appear only once something had been typed that matched
   * nothing, which meant the way to add a company was hidden behind knowing
   * that the company was not there — you had to type a name, watch the list
   * come back empty, and only then be told you could make one. The field
   * kit's whole point is that the way out is visible from the start, so the
   * row sits at the bottom of an open menu from the moment it opens: "Create
   * a new company" with nothing typed, "Create “Keys” as a new company" once
   * there is.
   *
   * The one time it is not offered is when what has been typed is already
   * there exactly — offering it then is how a catalog ends up with
   * "Mulch & soil" twice.
   */
  const exactMatch = trimmed.length > 0
    && items.some((item) => item.name.toLowerCase() === trimmed.toLowerCase())
  const canCreate = Boolean(onCreate) && !exactMatch
  const rows = canCreate ? items.length + 1 : items.length

  useLayoutEffect(() => {
    if (!open) { setBox(null); return }
    const place = () => {
      const rect = field.current?.getBoundingClientRect()
      if (!rect) return
      const at = placeFloating({
        anchor: rect,
        width: rect.width,
        wanted: Math.min(naturalHeight(panel.current, scroller.current), MENU_MAX),
      })
      /*
       * Only when it actually moved. This runs on every scroll event on the
       * page, and a fresh object each time is a re-render of the whole
       * lookup — including the list somebody is in the middle of scrolling.
       */
      setBox((current) => (current
        && current.left === at.left
        && current.top === at.top
        && current.width === rect.width
        && current.maxHeight === at.maxHeight
        ? current
        : { left: at.left, top: at.top, width: rect.width, maxHeight: at.maxHeight }))
    }
    place()

    /*
     * Scrolling the menu's own rows does not move the field it hangs off, so
     * there is nothing to re-place. Everything else — the page, the sheet the
     * field is in — genuinely moves the anchor and has to be followed.
     */
    const onScroll = (event: Event) => {
      const target = event.target
      if (target instanceof Node && panel.current?.contains(target)) return
      place()
    }

    window.addEventListener('scroll', onScroll, true)
    window.addEventListener('resize', place)
    return () => {
      window.removeEventListener('scroll', onScroll, true)
      window.removeEventListener('resize', place)
    }
  }, [open, items.length, canCreate, note])

  function close() {
    setOpen(false)
    setTyping(false)
    setQuery('')
  }

  async function create() {
    /*
     * With nothing typed there is nothing to name, so the row puts the caret
     * back in the box rather than creating a nameless record.
     */
    if (!onCreate) return
    if (!trimmed && !createOpens) { setOpen(true); typedInto.current?.focus(); return }

    /* Captured before `close()` below wipes the query. */
    const name = trimmed
    if (createOpens) close()

    setBusy(true)
    try {
      const made = await onCreate(name)
      if (made) { onPick(made); close() }
    } finally {
      setBusy(false)
    }
  }

  function choose(index: number) {
    if (canCreate && index === items.length) { void create(); return }
    const item = items[index]
    if (item) { onPick(item); close() }
  }

  function onKeyDown(event: React.KeyboardEvent) {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault()
      if (!open) { setOpen(true); return }
      if (rows === 0) return
      const step = event.key === 'ArrowDown' ? 1 : -1
      setActive((at) => (at + step + rows) % rows)
      return
    }
    if (event.key === 'Enter' && open) {
      event.preventDefault()
      choose(active)
      return
    }
    if (event.key === 'Escape' && open) {
      event.preventDefault()
      event.stopPropagation()
      close()
    }
  }

  /*
   * What the box shows: the chosen thing's name when it is not being typed
   * in, and the query while it is. Drawing the query over the selection would
   * make the field look empty the moment somebody clicks into it to check
   * what it says.
   */
  const shown = typing ? query : value?.name ?? ''

  return (
    <>
      <span ref={field} className={controlClass({ invalid, disabled, size })}>
        <input
          ref={typedInto}
          id={inputId}
          role="combobox"
          aria-expanded={open}
          aria-controls={open ? listId : undefined}
          aria-autocomplete="list"
          aria-label={label}
          aria-describedby={describedBy}
          aria-invalid={invalid || undefined}
          autoComplete="off"
          disabled={disabled}
          value={shown}
          placeholder={placeholder}
          /*
           * Clicking the field opens it; landing in it does not.
           *
           * `Sheet` focuses the first field when a form opens, and a combo
           * that opens on focus meant "New job" appeared with a dropdown
           * already covering the whole form. Typing, clicking and ArrowDown
           * all open it, which is every way somebody actually asks.
           */
          onMouseDown={() => setOpen(true)}
          /*
           * Leaving without picking puts back what is actually selected.
           *
           * Typed text that was never chosen is a field that looks filled and
           * saves nothing — the commonest way a lookup lies. The menu rows
           * refuse focus (`onMouseDown` below), so a blur here means focus
           * really left the field rather than moved into the list.
           */
          onBlur={() => { setTyping(false); setQuery('') }}
          onChange={(event) => {
            setTyping(true)
            setQuery(event.target.value)
            setOpen(true)
          }}
          onKeyDown={onKeyDown}
          className={cn('min-w-0 flex-1 border-none bg-transparent p-0 text-inherit',
            'placeholder:font-medium placeholder:text-text-faint',
            'focus:outline-none focus:ring-0')}
        />
        <span aria-hidden className="ml-auto shrink-0 pl-2 text-text-muted">
          <IconSearch size={14} />
        </span>
      </span>

      {open && box && createPortal(
        <div
          ref={panel}
          id={listId}
          role="listbox"
          aria-label={label}
          style={{
            left: box.left, top: box.top, width: box.width, maxHeight: box.maxHeight,
          }}
          /*
           * The menu never takes focus. Without this the field blurs on
           * mousedown, the blur handler above puts the selection back, and
           * the row unmounts before its click ever fires.
           */
          onMouseDown={(event) => event.preventDefault()}
          className={cn('fixed z-[80] flex flex-col overflow-hidden rounded-popup border',
            'border-line bg-surface py-1.5 shadow-popover')}
        >
          {/* The rows scroll; the create row below them does not. */}
          <div ref={scroller} className="min-h-0 flex-1 overflow-y-auto">
            {items.map((item, index) => (
              <Fragment key={item.id}>
                {item.group && item.group !== items[index - 1]?.group && (
                  <p className={cn('m-0 px-[18px] pb-1',
                    index === 0 ? 'pt-1' : 'mt-1 border-t border-line-faint pt-2.5',
                    'text-[10.5px] font-semibold uppercase tracking-[0.05em] text-text-faint')}>
                    {item.group}
                  </p>
                )}
                <button
                  type="button"
                  role="option"
                  aria-selected={index === active}
                  onMouseEnter={() => setActive(index)}
                  onClick={() => choose(index)}
                  style={{ minHeight: ROW_HEIGHT }}
                  className={cn('mx-1.5 flex w-[calc(100%-12px)] items-center gap-2.5',
                    'rounded-[8px] px-3 py-2 text-left',
                    index === active ? 'bg-accent-soft' : 'hover:bg-hover')}
                >
                  {item.lead && (
                    <span className={cn('flex h-[26px] w-[26px] shrink-0 items-center',
                      'justify-center rounded-tile-small bg-line-soft text-text-body')}>
                      {item.lead}
                    </span>
                  )}
                  <span className="flex min-w-0 flex-col gap-px">
                    <b className="truncate text-[13.5px] font-bold text-text">{item.name}</b>
                    {item.sub && (
                      <span className="truncate text-fieldlabel text-text-muted">{item.sub}</span>
                    )}
                  </span>
                </button>
              </Fragment>
            ))}

            {items.length === 0 && !canCreate && (
              <p className="m-0 px-[18px] py-2.5 text-[12.5px] text-text-muted">
                {empty ?? `Nothing matches “${trimmed}”.`}
              </p>
            )}

            {/*
              * How much of it you are looking at. Inside the scroller rather
              * than pinned under it, because it belongs to the rows — it is
              * the end of the list, and reading it means you have reached
              * the end.
              */}
            {note && items.length > 0 && (
              <p className={cn('m-0 px-[18px] pb-1 pt-2 text-fieldlabel text-text-faint')}>
                {note}
              </p>
            )}
          </div>

          {canCreate && (
            <button
              type="button"
              role="option"
              aria-selected={active === items.length}
              disabled={busy}
              onMouseEnter={() => setActive(items.length)}
              onClick={() => void create()}
              className={cn('flex w-full items-center gap-2 px-[18px] py-[9px] text-left',
                'text-[13px] font-semibold text-accent hover:text-accent-dark',
                items.length > 0 && 'border-t border-line-faint',
                active === items.length && 'bg-accent-soft')}
            >
              <IconPlus size={14} />
              {busy
                ? `Creating “${trimmed}”…`
                : trimmed
                  ? `Create “${trimmed}” as a new ${thing}`
                  : `Create a new ${thing}`}
            </button>
          )}
        </div>,
        document.body,
      )}
    </>
  )
}
